import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useBookingQueries } from '@/hooks/queries/useBookingQueries'
import { usePropertyQueries } from '@/hooks/queries/usePropertyQueries'
import { formatFullAddress, formatPrice } from '@/lib/utils'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
  AlertTriangle,
  ArrowLeft,
  Home,
  X
} from 'lucide-react'
import { useState } from 'react'

export const Route = createFileRoute('/_public/bookings/cancel/$bookingId')({
  component: CancelBookingPage,
})

function CancelBookingPage() {
  const [step, setStep] = useState<'confirm' | 'cancelled'>('confirm')
  const [isLoading, setIsLoading] = useState(false)

  const { bookingId } = useParams({ from: "/_public/bookings/cancel/$bookingId" })
  const { useFetchBooking, updateBookingMutation } = useBookingQueries()
  const { useFetchProperty } = usePropertyQueries()

  const { data: booking } = useFetchBooking(bookingId)
  const { data: property } = useFetchProperty(booking?.property_id || '')

  const handleCancelBooking = async () => {
    if (!booking) return

    setIsLoading(true)
    try {
      await updateBookingMutation.mutateAsync({
        bookingId,
        data: {
          status: 'cancelled',
        }
      }, {
        onSuccess: () => {
          setStep('cancelled')
        }
      })
    } catch (error) {
      console.error('Failed to cancel booking:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!booking || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 to-blue-50">
        <p className="text-slate-700 text-lg">Loading booking details...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 border-l border-slate-300" />
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Home className="h-4 w-4" />
                {step === 'confirm' ? 'Cancel Booking' : 'Booking Cancelled'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step === 'confirm' && (
          <div className="space-y-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-4xl font-semibold text-slate-900">Cancel Booking</h1>
              <p className="text-lg text-slate-600">
                Are you sure you want to cancel this booking?
              </p>
            </div>

            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Booking Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Property Info */}
                <div className="flex items-center gap-4">
                  <img
                    src={property.images?.[0].original_url || 'https://via.placeholder.com/100x100?text=No+Image'}
                    alt={formatFullAddress(property)}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-slate-900">{formatFullAddress(property)}</h3>
                    <p className="text-slate-600">{formatPrice(property.list_price)}</p>
                    <Badge variant="secondary">
                      {property.is_freehold ? property.freehold_property_type : property.condo_property_type}
                    </Badge>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Date & Time</p>
                      <p className="text-slate-900">{format(new Date(booking.start_datetime), 'EEEE, MMMM d, yyyy')}</p>
                      <p className="text-slate-900">{format(new Date(booking.start_datetime), 'hh:mm a')} - {format(new Date(booking.end_datetime), 'hh:mm a')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Visitor</p>
                      <p className="text-slate-900">{booking.visitor_name}</p>
                      <p className="text-slate-600">{booking.visitor_email}</p>
                      <p className="text-slate-600">{booking.visitor_phone}</p>
                    </div>
                  </div>
                </div>

                {booking.notes && (
                  <div className="border-t border-slate-200 pt-6">
                    <p className="text-sm font-medium text-slate-500 mb-1">Notes</p>
                    <p className="text-slate-900">{booking.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="text-center space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl mx-auto">
                <p className="text-red-800 text-sm">
                  <strong>Warning:</strong> This action cannot be undone. The booking will be permanently cancelled and all parties will be notified.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                  className="px-8 py-3"
                >
                  Keep Booking
                </Button>
                <Button
                  onClick={handleCancelBooking}
                  disabled={isLoading}
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Cancel Booking
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
        }

        {
          step === 'cancelled' && (
            <div className="max-w-2xl mx-auto text-center space-y-8 py-12">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-semibold text-slate-900 mb-2">Booking Cancelled</h1>
              <p className="text-lg text-slate-600">
                Your booking for{' '}
                <strong>{property ? formatFullAddress(property) : 'the property'}</strong>
                {' on '}
                <strong>{booking ? format(new Date(booking.start_datetime), 'EEEE, MMMM d, yyyy') : 'the scheduled date'}</strong>
                {' at '}
                <strong>{booking ? format(new Date(booking.start_datetime), 'hh:mm a') : 'the scheduled time'}</strong>
                {' has been successfully cancelled.'}
              </p>
              <p className="text-slate-500 mt-4">
                You will receive an email confirmation shortly.
              </p>
              <Button
                variant="outline"
                onClick={() => window.history.back()}
                className="mt-8 px-8 py-3"
              >
                Go Back
              </Button>
            </div>
          )
        }
      </div >
    </div >
  )
}
